import { Loader } from "components";
import { useFormik } from "formik";
import { useState } from "react";
import { number, object, ref, string } from 'yup';
import { AuthorizationService } from "services";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "hooks";

interface FormValues {
  password: string;
  confirmPassword: string;
  otp: string;
}

const ResetPasswordPage = () => {
    const [token, setToken] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const initialValues: FormValues = {
      password: "",
      confirmPassword: "",
      otp: "",
    };

    const validationSchema = object().shape({
      password: string().required("Password is required"),
      confirmPassword: string()
        .required("Confirm password is required")
        .oneOf([ref("password")], "Passwords must match"),
      otp: number().required("OTP is required"),
    });

    useEffectOnce(() => {
      const v = searchParams.get("token")!;
      setToken(v);
    });

    const { errors, values, touched, handleBlur, handleChange, handleSubmit } =
      useFormik({
        initialValues,
        validationSchema,
        onSubmit: async () => {
          setIsLoading(true);
          setErrorMessage("");
          AuthorizationService.ResetPassword({
            token,
            password: values.password,
            otp: values.otp,
          })
            .then((res) => {
              setIsLoading(false);
              navigate("/sign-in");
            })
            .catch((err) => {
              setIsLoading(false);
              if (err.response.status === 401) {
                setErrorMessage("Invalid email address or password");
                return;
              }
              setErrorMessage(
                "Something wrong happened. Please try again later"
              );
            });
        },
      });

    return (
      <div className="col-12 col-md-8 col-xl-6 mx-auto">
        <h2 className="text-center mb-4">Set a new password</h2>
        <form onSubmit={handleSubmit}>
          <p className="text-center">
            Create and confirm your new password below to regain access to your account. We have sent you an OTP to your number to help us know it's you.
          </p>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <div className="form-group mb-3">
                <label>Password</label>
                <input
                  name="password"
                  className={`form-input ${
                    Boolean(touched.password && errors.password)
                      ? "error-field"
                      : ""
                  }`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  type="password"
                  placeholder="Enter password"
                  required
                />
                {Boolean(touched.password && errors.password) && (
                  <p className="error mt-2"> {errors.password}</p>
                )}
              </div>
              <div className="form-group mb-3">
                <label>Confirm Password</label>
                <input
                  name="confirmPassword"
                  className={`form-input ${
                    Boolean(touched.confirmPassword && errors.confirmPassword)
                      ? "error-field"
                      : ""
                  }`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmPassword}
                  type="password"
                  placeholder="Enter password"
                  required
                />
                {Boolean(touched.confirmPassword && errors.confirmPassword) && (
                  <p className="error mt-2"> {errors.confirmPassword}</p>
                )}
              </div>
              <div className="form-group mb-3">
                <label>Enter OTP</label>
                <input
                  name="otp"
                  className={`form-input ${
                    Boolean(touched.otp && errors.otp) ? "error-field" : ""
                  }`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.otp}
                  type="text"
                  placeholder="Enter OTP code"
                  required
                />
                {Boolean(touched.otp && errors.otp) && (
                  <p className="error mt-2"> {errors.otp}</p>
                )}
              </div>
              <button className="btn btn-primary btn-full mt-3" type="submit">
                Reset Password
              </button>
            </>
          )}
        </form>
      </div>
    );
}

export default ResetPasswordPage;